import { Component, Inject, OnInit, OnDestroy, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ProduccionService } from 'app/services/produccion.service';
import { Subscription } from 'rxjs';

/**
 * Interfaz que define la estructura de datos para los insumos
 */
export interface Insumo {
  name: string;
  cantidadNecesaria: number;
  unidad: string;
  stock: number;
}

/**
 * Interfaz que define la estructura de los datos recibidos por el diálogo
 */
export interface ProductionRequirementsDialogData {
  idRecipe: number;
  quantity: number;
}

@Component({
    selector: 'app-production-requirements-dialog',
    template: `
        <div class="prod-req-container">
            <h2 mat-dialog-title>Requerimientos de Producción</h2>

            <!-- Spinner de Carga -->
            <div *ngIf="isLoading" class="prod-req-spinner-container">
                <mat-spinner diameter="50"></mat-spinner>
                <p>Comprobando stock. Espere...</p>
            </div>

            <!-- Contenido Principal (Tabla, Advertencia, Acciones) -->
            <div *ngIf="!isLoading && hayFaltantes">
                <p class="prod-req-warning">Existen faltantes en algunos de los insumos. ¿Desea continuar?</p>
                <mat-dialog-content class="prod-req-dialog-content">
                    <table mat-table [dataSource]="insumos" class="mat-elevation-z1">
                        <!-- Definiciones de Columnas -->
                        <ng-container matColumnDef="nombre">
                            <th mat-header-cell *matHeaderCellDef> Insumo </th>
                            <td mat-cell *matCellDef="let insumo"> {{ insumo.name }} </td>
                        </ng-container>
                        <ng-container matColumnDef="cantidadNecesaria">
                            <th mat-header-cell *matHeaderCellDef> Necesario </th>
                            <td mat-cell *matCellDef="let insumo"> {{ insumo.cantidadNecesaria | number:'1.2-2' }} </td>
                        </ng-container>
                        <ng-container matColumnDef="unidad">
                            <th mat-header-cell *matHeaderCellDef> Unidad de Medida </th>
                            <td mat-cell *matCellDef="let insumo"> {{ insumo.unidad }} </td>
                        </ng-container>
                        <ng-container matColumnDef="stockActual">
                            <th mat-header-cell *matHeaderCellDef> En Stock </th>
                            <td mat-cell *matCellDef="let insumo" [class.prod-req-alert]="insumo.stock < insumo.cantidadNecesaria">
                                {{ insumo.stock | number:'1.2-2' }}
                            </td>
                        </ng-container>
                        <tr mat-header-row *matHeaderRowDef="columnasTabla"></tr>
                        <tr mat-row *matRowDef="let row; columns: columnasTabla;"></tr>
                    </table>
                </mat-dialog-content>
                <div class="prod-req-actions">
                    <button mat-button (click)="close()">Cerrar</button>
                    <button mat-raised-button color="warn" (click)="guardar()">Continuar</button>
                </div>
            </div>
            <!-- Nota: Si !isLoading y !hayFaltantes, no se muestra nada porque el diálogo se cierra automáticamente -->
        </div>
    `,
    styles: [`
        .prod-req-container {
            padding: 20px 30px 30px;
        }
        .prod-req-dialog-content {
            max-height: 400px;
            overflow: auto;
        }
        .prod-req-warning {
            color: red;
        }
        table {
            width: 100%;
        }
        .prod-req-alert {
            color: red;
            font-weight: bold;
        }
        .prod-req-actions {
            display: flex;
            justify-content: space-between;
            margin-top: 16px;
        }
        table tr {
            height: 35px;
        }
        table th {
            background-color: #f5f5f5;
            border: 1px solid #f5f5f5;
        }
        .prod-req-spinner-container {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            padding: 40px 20px;
            min-height: 150px;
        }
        .prod-req-spinner-container p {
            margin-top: 15px;
            font-style: italic;
            color: grey;
        }
    `],
    encapsulation: ViewEncapsulation.Emulated
})
export class ProductionRequirementsDialogComponent implements OnInit, OnDestroy {
    /** Lista de insumos para la receta */
    insumos: Insumo[] = [];
    
    /** Indica si se está cargando la información */
    isLoading = true;
    
    /** Indica si hay faltantes en los insumos necesarios */
    hayFaltantes = false;
    
    /** Columnas a mostrar en la tabla */
    readonly columnasTabla: string[] = ['nombre', 'cantidadNecesaria', 'unidad', 'stockActual'];
    
    /** Suscripciones del componente para prevenir fugas de memoria */
    private subscriptions: Subscription[] = [];

    /**
     * Constructor del componente
     * @param dialogRef Referencia al diálogo
     * @param data Datos recibidos por el diálogo
     * @param produccionService Servicio de producción
     */
    constructor(
        public dialogRef: MatDialogRef<ProductionRequirementsDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: ProductionRequirementsDialogData,
        private produccionService: ProduccionService
    ) { }

    /**
     * Inicializa el componente y obtiene los insumos necesarios
     */
    ngOnInit(): void {
        this.getStockInsumosByReceta(this.data.idRecipe, this.data.quantity);
    }

    /**
     * Obtiene el stock de insumos necesarios para la receta
     * @param idRecipe ID de la receta
     * @param quantity Cantidad a producir
     */
    private getStockInsumosByReceta(idRecipe: number, quantity: number = 1): void {
        this.isLoading = true;
        this.hayFaltantes = false;
        this.insumos = [];
        
        const subscription = this.produccionService.getStockInsumosByReceta(idRecipe, quantity).subscribe({
            next: (data: Insumo[]) => {
                this.insumos = data;
                this.hayFaltantes = this.insumos.some(insumo => insumo.stock < insumo.cantidadNecesaria);
                this.isLoading = false;
                
                // Solo cierra automáticamente si no hay faltantes
                if (!this.hayFaltantes) {
                    this.dialogRef.close(true);
                }
            },
            error: (error) => {
                this.isLoading = false;
                console.error('Error al obtener insumos de la receta:', error);
                this.dialogRef.close(false);
            }
        });
        
        // Registrar la suscripción para cancelarla durante la destrucción
        this.subscriptions.push(subscription);
    }
    
    /**
     * Limpieza al destruir el componente para evitar fugas de memoria
     */
    ngOnDestroy(): void {
        // Cancelar todas las suscripciones
        this.subscriptions.forEach(sub => {
            if (sub && !sub.closed) {
                sub.unsubscribe();
            }
        });
        this.subscriptions = [];
    }

    /**
     * Cierra el diálogo indicando cancelación del usuario
     */
    close(): void {
        try {
            this.dialogRef.close(false);
        } catch (error) {
            console.error('Error al cerrar el diálogo:', error);
        }
    }

    /**
     * Cierra el diálogo indicando que el usuario desea continuar a pesar de los faltantes
     */
    guardar(): void {
        try {
            this.dialogRef.close(true);
        } catch (error) {
            console.error('Error al cerrar el diálogo:', error);
        }
    }
}
